import type {
  DiagnosisContext,
  DiagnosisFinding,
  DiagnosisResult,
  DiagnosisRule,
} from './contracts';
import { calculateCoverage } from './coverage';
import { DIAGNOSIS_RULES, severityRank, validateRuleRegistry } from './registry';
import { calculateScores } from './scores';

function deduplicate(findings: readonly DiagnosisFinding[]): DiagnosisFinding[] {
  const byRoot = new Map<string, DiagnosisFinding>();
  for (const item of findings) {
    const root = `${item.ruleId}:${[...item.affectedNodeIds].sort().join(',')}:${[...item.affectedEdgeIds].sort().join(',')}`;
    const previous = byRoot.get(root);
    if (!previous) {
      byRoot.set(root, item);
      continue;
    }
    byRoot.set(root, {
      ...previous,
      evidence: [...previous.evidence, ...item.evidence].filter(
        (evidence, index, all) =>
          all.findIndex((candidate) => JSON.stringify(candidate) === JSON.stringify(evidence)) ===
          index,
      ),
      caveats: [...new Set([...previous.caveats, ...item.caveats])],
      basicSignalRefs: [...new Set([...previous.basicSignalRefs, ...item.basicSignalRefs])],
    });
  }
  return [...byRoot.values()];
}

export function runDiagnosis(
  context: DiagnosisContext,
  now = new Date(),
  rules: readonly DiagnosisRule[] = DIAGNOSIS_RULES,
): DiagnosisResult {
  const startedAt = performance.now();
  const registryErrors = validateRuleRegistry(rules);
  if (registryErrors.length) throw new Error(registryErrors.join(' '));
  const coverage = calculateCoverage(context);
  const rulePriority = new Map(rules.map((rule) => [rule.id, rule.priority]));
  const ruleWarnings: string[] = [];
  const matchedRules = new Set<string>();
  const evaluatedFindings = rules.flatMap((rule) => {
    try {
      const findings = rule.evaluate(context, coverage).filter((item) => {
        if (item.severity !== 'critical' || item.evidence.length > 0) return true;
        ruleWarnings.push(`Rule ${rule.id} returned a critical finding without evidence.`);
        return false;
      });
      if (findings.length) matchedRules.add(rule.id);
      return findings;
    } catch {
      ruleWarnings.push(`Rule ${rule.id} could not be evaluated safely.`);
      return [];
    }
  });
  const items = deduplicate(evaluatedFindings).sort(
    (left, right) =>
      severityRank(right.severity) - severityRank(left.severity) ||
      (right.impactScore ?? -1) - (left.impactScore ?? -1) ||
      right.confidence - left.confidence ||
      (rulePriority.get(right.ruleId) ?? 0) - (rulePriority.get(left.ruleId) ?? 0) ||
      left.id.localeCompare(right.id),
  );
  const scores = calculateScores(context);
  return {
    items,
    summary: {
      critical: items.filter((item) => item.severity === 'critical').length,
      attention: items.filter((item) => item.severity === 'attention').length,
      opportunities: items.filter((item) => item.type === 'opportunity').length,
      dataGaps: items.filter((item) => item.type === 'data_gap').length,
      topPriorityIds: items.slice(0, 3).map((item) => item.id),
    },
    coverage,
    warnings: [
      ...ruleWarnings,
      ...scores.warnings,
      ...(coverage.overall < 0.4
        ? ['Diagnosis coverage is low; findings should be treated as directional.']
        : []),
    ],
    scores,
    evaluatedAt: now.toISOString(),
    runtime: {
      durationMs: Math.round((performance.now() - startedAt) * 1_000) / 1_000,
      rulesEvaluated: rules.length,
      rulesMatched: matchedRules.size,
    },
    context: {
      scenario: context.scenario ?? { id: 'base', name: 'Base', type: 'base' },
      ...(context.target ? { target: context.target } : {}),
    },
  };
}
